import AppliedFilters from "components/common/AppliedFilters";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { useMemo, useState } from "react";
import FilterPillPopup from "components/common/FilterPillPopup";
import FilterPill from "components/common/FilterPill";
import './agentsAppliedFilters.scss';

const STATUS_OPTIONS = [
    { value: 'Available', label: 'Available' },
    { value: 'Ringing', label: 'Ringing' },
    { value: 'In a queue call', label: 'In a queue call' },
    { value: 'Logged Out', label: 'Logged Out' },
    { value: 'DND', label: 'DND' },
];

interface AgentsAppliedFiltersProps {
    handleClearFilters: () => void;
    handleRemoveFilter?: (key: string) => void;
    filters: any;
    onFilterUpdate?: (key: string, value: any) => void;
}

export default function AgentsAppliedFilters({ 
    handleClearFilters, 
    handleRemoveFilter, 
    filters,
    onFilterUpdate 
}: AgentsAppliedFiltersProps) {
    const queues = useSelector((state: RootState) => state.apps.data.queue || []);
    const agents = useSelector((state: RootState) => state.agents.items);

    // Create a map of queue extensions to names
    const queueMap = useMemo(() => {
        const map = new Map<string, string>();
        queues.forEach((queue: any) => {
            map.set(queue.extension, queue.name || queue.extension);
        });
        return map;
    }, [queues]);

    // Create queue options
    const queueOptions = useMemo(() => {
        return queues.map((queue: any) => ({ 
            label: queue.name || queue.extension, 
            value: queue.extension 
        }));
    }, [queues]);

    // Create tier options
    const tierOptions = useMemo(() => {
        const tierSet = new Set<number>();
        agents.forEach((agent: any) => {
            if (agent.level && Array.isArray(agent.level)) {
                agent.level.forEach((tier: number) => tierSet.add(tier));
            }
        });
        return Array.from(tierSet).sort((a, b) => a - b).map(tier => ({
            label: `Tier ${tier}`,
            value: String(tier)
        }));
    }, [agents]);

    const handleDeselectItem = (filterKey: string, value: string) => {
        if (!onFilterUpdate) return;
        
        const currentValues = filters[filterKey] || [];
        const updatedValues = currentValues.filter((v: string) => v !== value);
        
        if (updatedValues.length === 0) {
            onFilterUpdate(filterKey, null);
        } else {
            onFilterUpdate(filterKey, updatedValues);
        }
    };

    const handleClearFilter = (filterKey: string) => {
        if (onFilterUpdate) {
            onFilterUpdate(filterKey, null);
        }
    };

    const hasAny = Boolean(
        (filters.status && filters.status.length) ||
        (filters.queue && filters.queue.length) ||
        (filters.tier && filters.tier.length)
    )

    if (!hasAny) return null

    const pills: { key: string; label: string, value?: string, selectedValues?: string[], options?: { label: string; value: string }[] }[] = []
    
    if (filters.status && filters.status.length) {
        const statusNames = filters.status.map((s: string) => 
            STATUS_OPTIONS.find(opt => opt.value === s)?.label || s
        );
        // Show count if more than 1, otherwise show the name
        const statusValue = statusNames.length === 1 
            ? statusNames[0] 
            : `${statusNames.length} selected`;
        
        pills.push({ 
            key: 'status', 
            label: 'Status',
            value: statusValue,
            selectedValues: filters.status,
            options: STATUS_OPTIONS
        })
    }

    if (filters.queue && filters.queue.length) {
        const queueNames = filters.queue
            .map((ext: string) => queueMap.get(ext) || ext)
            .filter(Boolean);
        
        // Show count if more than 1, otherwise show the name
        const queueValue = queueNames.length === 1 
            ? queueNames[0] 
            : `${queueNames.length} selected`;
        
        pills.push({ 
            key: 'queue', 
            label: 'Queue',
            value: queueValue,
            selectedValues: filters.queue,
            options: queueOptions
        })
    }

    if (filters.tier && filters.tier.length) {
        const tierLabels = filters.tier.map((t: string) => `Tier ${t}`).join(', ');
        // Show count if more than 1, otherwise show the tier label
        const tierValue = filters.tier.length === 1 
            ? tierLabels 
            : `${filters.tier.length} selected`;
        
        pills.push({ 
            key: 'tier', 
            label: 'Tier',
            value: tierValue,
            selectedValues: filters.tier,
            options: tierOptions
        })
    }

    return (
        <div className="agents-applied-filters-wrapper">
            <p className="filter-label">Filters:</p>
            {pills.map((pill) => {
                const pillContent = (
                    <FilterPill 
                        key={pill.key} 
                        name={pill.label} 
                        onRemove={() => handleRemoveFilter?.(pill.key)} 
                        value={pill.value}
                    />
                );

                if (pill.selectedValues && pill.selectedValues.length > 0 && pill.options && onFilterUpdate) {
                    return (
                        <FilterPillPopup
                            key={pill.key}
                            filterKey={pill.key}
                            filterLabel={pill.label}
                            selectedValues={pill.selectedValues}
                            options={pill.options}
                            onDeselect={(value) => handleDeselectItem(pill.key, value)}
                            onClearAll={() => handleClearFilter(pill.key)}
                        >
                            <div style={{ display: 'inline-block' }}>
                                {pillContent}
                            </div>
                        </FilterPillPopup>
                    );
                }

                return pillContent;
            })}
            <button className="clear-filter-button" onClick={handleClearFilters}>Clear All</button>
        </div>
    )
}

